import { useState, useEffect } from "react";
import { Nav, Button } from "react-bootstrap";

import TablaBacSarampion from "../pages/TablaBacSarampion";
import MapaBacSarampion  from "../pages/MapaBacSarampion";
import DashboardBacSarampion from "../pages/DashboardBacSarampion";
import TablaPaiVacunacion from "../pages/TablaPaiVacunacion";
import TablaMrcv from "../pages/TablaMrcv";
import EstadoSincronizacionBac from "./EstadoSincronizacionBac";
import SincronizandoImagenesBac from "./SincronizandoImagenesBac";
import BarraActualizacionBac from "./BarraActualizacionBac";
import LiberarEspacioBac from "./LiberarEspacioBac";
import BacLogin from "./BacLogin";
import PageUsuariosBac from "../pages/PageUsuariosBac";
import TablaDisentimiento from "../disentimiento/TablaDisentimiento";
import TablaJornadasBac, { ResumenJornada } from "../jornadas/TablaJornadasBac";
import useJornadasBac from "../jornadas/useJornadasBac";
import { fmtFecha, precargarJornadasTodas } from "../jornadas/jornadasBac";
import "../bac.css";

import { useTerritorio } from "../../context/TerritorioContext";
import { useBacAuth } from "../../context/BacAuthContext";
import { useOnlineStatus, contarPendientesBac } from "../../hooks/useSync";
import TerritorioModal from "../modal/TerritorioModal";
import { useCatalogosListos, PantallaDescargandoListas, PantallaSinListas } from "./CargaInicialListas";
import { APP_VERSION } from "../../version";

export default function BacSarampionHome() {
  const [tab, setTab]     = useState("registro");
  const { data, setData } = useTerritorio();
  const { usuario, logueado, logout } = useBacAuth();
  const online             = useOnlineStatus();
  const showModal          = !data.territorio;
  const [pendientes, setPendientes] = useState(0);
  const [focoMapa, setFocoMapa] = useState(null);

  const esAdmin = usuario?.rol === "admin";

  // ── Flujo de ingreso: territorio → comunidad/barrio → JORNADA ──────
  // La jornada elegida queda guardada junto con el territorio y la
  // comunidad (sobrevive a recargar la página). Con ese contexto, TODAS
  // las pestañas muestran solo lo de esa jornada.
  const jornadasBac = useJornadasBac(logueado ? data.territorio : "", logueado ? data.comunidad : "");
  const jornadaCodigo = data.jornada_codigo || "";
  const norm = (c) => String(c || "").trim().toLowerCase();
  const jornadaSel = jornadaCodigo ? (jornadasBac.jornadas.find((j) => norm(j.codigo) === norm(jornadaCodigo)) || null) : null;
  const propsJornada = { jornadas: jornadasBac.jornadas, filtroJornada: jornadaCodigo };

  const ingresarAJornada = (j) => { setData({ ...data, jornada_codigo: j.codigo }); setTab("registro"); };
  const cambiarJornada = () => setData({ territorio: data.territorio, comunidad: data.comunidad });

  // Jornada abierta sin señal que al sincronizar se unió a otra: se sigue
  // dentro de la que quedó en el servidor.
  useEffect(() => {
    const f = (ev) => {
      const d = ev.detail || {};
      if (d.local && norm(d.local) === norm(jornadaCodigo)) setData({ ...data, jornada_codigo: d.servidor });
    };
    window.addEventListener("bac-jornada-fusionada", f);
    return () => window.removeEventListener("bac-jornada-fusionada", f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jornadaCodigo, data]);

  // capa: "visitas" (tabla de visitas) | "pai" (tabla de barrido PAI)
  const [capaMapa, setCapaMapa] = useState("visitas");
  const verEnMapa = (codigoVivienda, capa = "visitas") => {
    setFocoMapa(codigoVivienda);
    setCapaMapa(capa);
    setTab("mapa");
  };

  const { estado: estadoCatalogos, reintentar: reintentarCatalogos } = useCatalogosListos(logueado);

  // Todas las jornadas quedan guardadas en el dispositivo (para trabajar
  // sin señal en cualquier comunidad): al entrar con internet, al recuperar
  // la conexión y cada vez que termina una sincronización.
  useEffect(() => {
    if (!logueado) return;
    const precargar = () => { if (navigator.onLine) precargarJornadasTodas(); };
    precargar();
    window.addEventListener("online", precargar);
    window.addEventListener("sync-completado", precargar);
    return () => {
      window.removeEventListener("online", precargar);
      window.removeEventListener("sync-completado", precargar);
    };
  }, [logueado]);

  // Los modales se pintan fuera del módulo (en <body>): esta clase les
  // aplica el mismo estilo de la BAC solo mientras se está en la BAC.
  useEffect(() => {
    document.body.classList.add("bac-body");
    return () => document.body.classList.remove("bac-body");
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const actualizar = () => contarPendientesBac().then(setPendientes);
    actualizar();
    window.addEventListener("sync-completado", actualizar);
    return () => window.removeEventListener("sync-completado", actualizar);
  }, []);

  if (!logueado) {
    return <BacLogin />;
  }

  // Si por algún motivo el rol quedó vacío/corrupto en la sesión
  // guardada, se trata como gestor (el más restrictivo), por seguridad.
  const rolSeguro = esAdmin ? "admin" : "gestor";

  if (estadoCatalogos === "verificando" || estadoCatalogos === "descargando") {
    return <PantallaDescargandoListas />;
  }
  if (estadoCatalogos === "sin_datos") {
    return <PantallaSinListas onReintentar={reintentarCatalogos} />;
  }

  const enJornada = !!(data.territorio && jornadaCodigo);
  const est = jornadaSel?.estado;

  const rangoJornada = jornadaSel
    ? (jornadaSel.fecha_fin && jornadaSel.fecha_fin !== jornadaSel.fecha_inicio
        ? `${fmtFecha(jornadaSel.fecha_inicio)} – ${fmtFecha(jornadaSel.fecha_fin)}`
        : jornadaSel.fecha_fin ? fmtFecha(jornadaSel.fecha_inicio) : `Desde ${fmtFecha(jornadaSel.fecha_inicio)}`)
    : (jornadasBac.listo && !jornadasBac.cargando ? "No se encontró" : "Cargando…");
  const ESTADO_TXT = { abierta: "Abierta", cerrada: "Cerrada · solo consulta", programada: "Programada" };

  const PESTANAS = [
    ["resumen", "Resumen", "M3 13h4v8H3zM10 3h4v18h-4zM17 8h4v13h-4z"],
    ["registro", "Visitas", "M3 11l9-8 9 8M5 10v10h14V10"],
    ["pai", "Barrido PAI", "M18 2l4 4M15 5l4 4M3 21l3-3M6 18l-2-2 9-9 4 4-9 9zM9 11l2 2"],
    ["mrcv", "MRCV", "M9 4h6v3H9zM6 6h12v15H6zM9 12h6M9 16h4"],
    ["mapa", "Mapa", "M9 4L3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14"],
    ["disentimiento", "Disentimiento PAI", "M4 20h4L19 9l-4-4L4 16zM13 7l4 4"],
    ...(rolSeguro === "admin" ? [["usuarios", "Usuarios", "M16 20v-2a4 4 0 0 0-8 0v2M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8"]] : []),
  ];

  return (
    <div className="bac-app">
      <BarraActualizacionBac />
      <TerritorioModal show={showModal} />

      {/* Cabecera del módulo */}
      <header className="bac-cabecera">
        <div className="bac-marca">
          <h1>Búsqueda activa comunitaria</h1>
          <p>Sarampión y rubéola</p>
        </div>
        <div className="bac-usuario">
          {!online && <span className="bac-etiqueta is-aviso">Sin conexión: se guarda en el dispositivo</span>}
          {pendientes > 0 && <span className="bac-etiqueta is-info">{pendientes} por sincronizar</span>}
          <span className="bac-usuario-nombre">{usuario?.nombre}<small>{rolSeguro === "admin" ? "Administrador" : "Gestor"}</small></span>
          <Button variant="outline-secondary" size="sm" onClick={logout}>Salir</Button>
        </div>
      </header>

      {/* Dónde se está trabajando: territorio → comunidad → jornada */}
      {data.territorio && (
        <nav className="bac-contexto" aria-label="Contexto de trabajo">
          <button type="button" className="bac-contexto-paso" onClick={() => setData({ territorio: "", comunidad: "" })} title="Cambiar territorio y comunidad">
            <small>Territorio</small><span>{data.territorio}</span>
          </button>
          <button type="button" className="bac-contexto-paso" onClick={() => setData({ territorio: "", comunidad: "" })} title="Cambiar territorio y comunidad">
            <small>Comunidad / barrio</small><span>{data.comunidad}</span>
          </button>
          {enJornada ? (
            <div className={`bac-contexto-paso is-jornada is-${est || "cargando"}`}>
              <small>Jornada{jornadaSel ? ` · ${ESTADO_TXT[est] || est}` : ""}</small>
              <span>{rangoJornada}{jornadaSel?.lider_nombre ? <em> · Líder {jornadaSel.lider_nombre}</em> : null}</span>
            </div>
          ) : (
            <div className="bac-contexto-paso is-pendiente"><small>Jornada</small><span>Elige una jornada</span></div>
          )}
          <div className="bac-contexto-acciones">
            {enJornada && <Button size="sm" variant="outline-secondary" onClick={cambiarJornada}>Cambiar jornada</Button>}
            <Button size="sm" variant="outline-secondary" onClick={() => setData({ territorio: "", comunidad: "" })}>Cambiar territorio</Button>
          </div>
        </nav>
      )}

      <EstadoSincronizacionBac />
      <SincronizandoImagenesBac />

      {/* Paso 3 del ingreso: elegir (o abrir) la jornada */}
      {data.territorio && !enJornada && (
        <div className="mt-3">
          <TablaJornadasBac ctx={data} usuario={usuario} esAdmin={rolSeguro === "admin"} online={online}
            jornadasBac={jornadasBac} onVerVisitas={ingresarAJornada} />
        </div>
      )}

      {enJornada && (
        <>
          <Nav activeKey={tab} onSelect={setTab} className="bac-pestanas" as="nav">
            {PESTANAS.map(([k, t, d]) => (
              <Nav.Item key={k}>
                <Nav.Link eventKey={k}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
                  {t}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <div className="mt-3">
            {tab === "resumen" && (
              <>
                <ResumenJornada jornada={jornadaSel} ctx={data} usuario={usuario} esAdmin={rolSeguro === "admin"} online={online} />
                <DashboardBacSarampion filtroJornada={jornadaCodigo} />
              </>
            )}
            <div style={{ display: tab === "registro" ? "block" : "none" }}>
              <TablaBacSarampion onVerEnMapa={verEnMapa} puedeExportar={rolSeguro === "admin"} esAdmin={rolSeguro === "admin"}
                jornadaActiva={est === "abierta" ? jornadaSel : null} {...propsJornada} />
            </div>
            {tab === "pai" && <TablaPaiVacunacion onVerEnMapa={verEnMapa} puedeExportar={rolSeguro === "admin"} esAdmin={rolSeguro === "admin"} {...propsJornada} />}
            {tab === "mrcv" && <TablaMrcv puedeExportar={rolSeguro === "admin"} esAdmin={rolSeguro === "admin"} {...propsJornada} />}
            {/* El mapa (Leaflet) SÍ necesita desmontarse/montarse de verdad, no
                solo ocultarse con display:none — si se inicializa oculto, el
                contenedor mide 0x0 y el mapa nunca carga bien los tiles. */}
            {tab === "mapa" && (
              <MapaBacSarampion
                focoCodigoVivienda={focoMapa}
                capaInicial={capaMapa}
                onFocoConsumido={() => setFocoMapa(null)}
                {...propsJornada}
              />
            )}
            {tab === "disentimiento" && <TablaDisentimiento esAdmin={rolSeguro === "admin"} />}
            {rolSeguro === "admin" && tab === "usuarios" && <PageUsuariosBac />}
          </div>
        </>
      )}

      <div className="bac-pie">
        <span>BAC Sarampión · versión {APP_VERSION}</span>
        <LiberarEspacioBac />
      </div>
    </div>
  );
}
